export default class Option<T> {
  _value: T | null;

  static of<T>(value: (T | null) | Option<T>): Option<T> {
    if (value instanceof Option) {
      return value;
    }
    return new Option(value);
  }

  static none<T>(): Option<T> {
    return new Option(null);
  }

  static zip<T, U>(optionA: Option<T>, optionB: Option<U>): Option<[T, U]> {
    if (optionA.hasValue() && optionB.hasValue()) {
      return Option.of([optionA.get(), optionB.get()]);
    }
    return Option.none();
  }

  constructor(value: T | null) {
    this._value = value;
  }

  hasValue(): boolean {
    return this._value != null;
  }

  when(func: (arg0: T) => void): Option<T> {
    if (this._value != null) {
      func(this._value);
    }
    return this;
  }

  map<U>(mapper: (value: T) => U | null): Option<U> {
    if (this._value != null) {
      return Option.of(mapper(this._value));
    }
    return Option.none();
  }

  flatMap<U>(mapper: (value: T) => Option<U>): Option<U> {
    if (this._value != null) {
      return mapper(this._value);
    }
    return Option.none();
  }

  chain<U>(mapper: (value: T) => U | null): Option<Array<T | U>> {
    const { _value } = this;
    if (_value != null) {
      const mappedValue = mapper(_value);
      if (mappedValue != null) return Option.of([_value, mappedValue]);
    }
    return Option.none();
  }

  flatChain<U>(mapper: (value: T) => Option<U>): Option<[T, U]> {
    const { _value } = this;
    if (_value != null) {
      const mappedValue = mapper(_value);
      if (mappedValue.hasValue()) {
        return Option.of([_value, mappedValue.get()]);
      }
    }
    return Option.none();
  }
  flatChain2<U, V>(
    mapper1: (value: T) => Option<U>,
    mapper2: (value: T) => Option<V>,
  ): Option<[T, U, V]> {
    const { _value } = this;
    if (_value != null) {
      const mappedValue1 = mapper1(_value);
      const mappedValue2 = mapper2(_value);
      if (mappedValue1.hasValue() && mappedValue2.hasValue()) {
        return Option.of([_value, mappedValue1.get(), mappedValue2.get()]);
      }
    }
    return Option.none();
  }

  orElse<U>(func: () => U | null): Option<U> | Option<T> {
    if (this._value != null) {
      return this;
    }
    return Option.of(func());
  }

  getOrElse<U>(defaultValue: () => U): T | U {
    if (this._value != null) {
      return this._value;
    }
    return defaultValue();
  }

  get(): T {
    if (this._value != null) {
      return this._value;
    }
    throw new TypeError("Can't extract the value of a None.");
  }

  getNullable(): T | null {
    return this._value;
  }
}
